import {
  hasMemberErrors,
  normalizePlate,
  validateFamilyMember,
  type FamilyMemberErrors,
} from '../../components/notification-modal/utils/notification-family-sale.util';
import type {
  FamilyFormOption,
  FamilyFormPrice,
  FamilyMemberFormModel,
} from '../types/family-member-form.types';
import {
  formatFamilyMoney,
  previewMemberPrice,
} from '../../components/notification-modal/utils/notification-family-sale.util';
import { formatFamilyValue } from './family-plan-settings.util';
import type { FamilyPlanMemberView } from '../types/family-plan-view.types';

/** Pure rules for the Family Plan tab's "Edit Details" form: name, phone and plate of one member. */

/** What a save sends: only the parts that changed, to the customer and the vehicle. */
export interface FamilyMemberEditChange {
  customerId: string;
  vehicleId: string;
  customer: { first_name: string; last_name: string; contact: string } | null;
  vehicle: { license_plate: string; state: string } | null;
}

/** "Taufik Ahamed" -> first and last name, split on the first space. */
export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const text = (fullName || '').trim().replace(/\s+/g, ' ');
  const space = text.indexOf(' ');
  return space === -1
    ? { firstName: text, lastName: '' }
    : { firstName: text.slice(0, space), lastName: text.slice(space + 1) };
}

/** The form's starting values: what the member has today. */
export function editModelOf(member: FamilyPlanMemberView): FamilyMemberFormModel {
  const { firstName, lastName } = splitFullName(member.customer.name ?? '');
  return {
    firstName,
    lastName,
    fullName: member.customer.name ?? '',
    // The last ten digits: a stored +1 number must not fail the ten-digit phone rule untouched.
    phone: (member.customer.phone ?? '').replace(/\D/g, '').slice(-10),
    plate: member.vehicle.license_plate ?? '',
    state: member.vehicle.state ?? '',
    packageId: member.membership.package_id,
    discountValue: member.membership.discount?.value ?? null,
  };
}

/**
 * Field errors for the edit form. Package and discount are shown but not edited here, so they
 * never fail it; name and phone are skipped when the member's contact is the primary's.
 */
export function validateFamilyMemberEdit(
  model: FamilyMemberFormModel,
  takenPlates: string[],
  contactLocked: boolean
): FamilyMemberErrors {
  const { firstName, lastName } = splitFullName(model.fullName ?? '');
  const errors = validateFamilyMember(
    { ...model, firstName, lastName },
    takenPlates,
    model.discountValue === null ? [] : [model.discountValue]
  );
  delete errors.packageId;
  delete errors.discount;
  if (contactLocked) {
    delete errors.firstName;
    delete errors.lastName;
    delete errors.phone;
  }
  return errors;
}

export const hasEditErrors = hasMemberErrors;

/** The plates the edited one may not take: every other plate in the family. */
export function otherPlates(members: FamilyPlanMemberView[], editing: FamilyPlanMemberView) {
  return members
    .filter(member => member.member_id !== editing.member_id)
    .map(member => member.vehicle.license_plate ?? '')
    .filter(Boolean);
}

/** Only what changed; null for a part with no change, so a plate-only edit never touches the customer. */
export function editChangeOf(
  member: FamilyPlanMemberView,
  model: FamilyMemberFormModel,
  contactLocked: boolean
): FamilyMemberEditChange {
  const before = editModelOf(member);
  const { firstName, lastName } = splitFullName(model.fullName ?? '');
  const was = splitFullName(before.fullName ?? '');
  const customerChanged =
    !contactLocked &&
    (firstName !== was.firstName || lastName !== was.lastName || model.phone !== before.phone);
  const plate = normalizePlate(model.plate);
  const vehicleChanged = plate !== normalizePlate(before.plate) || model.state !== before.state;
  return {
    customerId: member.customer.id,
    vehicleId: member.vehicle.id,
    customer: customerChanged
      ? { first_name: firstName, last_name: lastName, contact: model.phone }
      : null,
    vehicle: vehicleChanged ? { license_plate: plate, state: model.state } : null,
  };
}

export const hasEditChange = (change: FamilyMemberEditChange): boolean =>
  change.customer !== null || change.vehicle !== null;

/** The package the member is on, as the form's one read-only option. */
export function editPackageOptions(member: FamilyPlanMemberView): FamilyFormOption[] {
  const { package_id: id, package_name: name, price } = member.membership;
  const label = [name, price === null ? null : `${formatFamilyMoney(price)}/mo`]
    .filter(Boolean)
    .join(' · ');
  return [{ value: id, label }];
}

/** The member's discount, as the form's one read-only option; none for a primary. */
export function editDiscountOptions(member: FamilyPlanMemberView): FamilyFormOption<number>[] {
  const discount = member.membership.discount;
  if (!discount) {
    return [];
  }
  const type = discount.type === 'flat' ? 'flat' : 'percentage';
  return [{ value: discount.value, label: formatFamilyValue(type, discount.value) }];
}

/** What the member pays each month, against the package's list price. */
export function editPrice(member: FamilyPlanMemberView): FamilyFormPrice | null {
  const { price, discount } = member.membership;
  if (price === null) {
    return null;
  }
  if (!discount) {
    return { recurringPrice: price, list: price };
  }
  const type = discount.type === 'flat' ? 'flat' : 'percentage';
  return {
    recurringPrice: previewMemberPrice(price, type, discount.value).recurringPrice,
    list: price,
  };
}
